import React, { useMemo } from "react";

import { ListItem, ListItemIcon, ListItemText } from "@mui/material";
import { makeStyles } from "tss-react/mui";

import { colors } from "@/theme/overrides/colors";

import NavLink from "../NavLink";

const useStyles = makeStyles()((theme) => ({
  listItem: {
    paddingTop: 20,
    paddingBottom: 20,
  },
  listItemSubItem: {
    paddingTop: 10,
    paddingBottom: 10,
    "& .MuiListItemText-primary": {
      fontSize: 14,
    },
  },
  listItemIcon: {
    color: colors.gray,
  },
  listItemTrailingIcon: {
    color: colors.gray,
    minWidth: 0,
  },
  listItemText: {
    color: colors.dark,
  },
  listItemTextPrimary: {
    fontWeight: 600,
    fontSize: 16,
    lineHeight: 1,
    letterSpacing: -0.5,
  },
  chevron: {
    color: colors.dark,
    minWidth: 0,
  },
  activeLink: {
    color: theme.palette.primary.main,
    "& .MuiListItemIcon-root": {
      color: "inherit",
    },
    "& .MuiListItemText-root": {
      color: "inherit",
    },
  },
}));

interface ListItemLinkProps {
  to: string;
  icon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  text: string;
  exact?: boolean;
  subItem?: boolean;
  onHover?: () => void;
  onHoverEnd?: () => void;
}

const ListItemLink: React.FC<ListItemLinkProps> = ({
  to,
  icon,
  trailingIcon,
  text,
  exact,
  subItem,
}) => {
  const { classes } = useStyles();

  const renderLink = useMemo(() => {
    const Link = React.forwardRef<HTMLAnchorElement>((itemProps, ref) => (
      <NavLink
        to={to}
        ref={ref}
        activeClassName={classes.activeLink}
        {...itemProps}
        end={exact}
      />
    ));
    Link.displayName = "Link";
    return Link;
  }, [to, exact]);

  return (
    <li>
      <ListItem
        className={`${classes.listItem} ${subItem && classes.listItemSubItem}`}
        component={renderLink}
      >
        {Boolean(icon) && (
          <ListItemIcon className={classes.listItemIcon}>{icon}</ListItemIcon>
        )}
        <ListItemText
          className={classes.listItemText}
          classes={{ primary: classes.listItemTextPrimary }}
          primary={text}
          inset={!icon}
        />
        {Boolean(trailingIcon) && (
          <ListItemIcon className={classes.listItemTrailingIcon}>
            {trailingIcon}
          </ListItemIcon>
        )}
      </ListItem>
    </li>
  );
};

export default ListItemLink;
